import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import ManageHistoryIcon from '@mui/icons-material/ManageHistory';
import SettingsSuggestIcon from '@mui/icons-material/SettingsSuggest';
import {
  Box,
  Button,
  Step,
  StepButton,
  Stepper,
  Typography,
} from '@mui/material';
import { googleLogout } from '@react-oauth/google';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import useEndDeploy from '../../hooks/system/useEndDeploy';
import useStartDeploy from '../../hooks/system/useStartDeploy';
import useSystemCancel from '../../hooks/system/useSystemCancel';
import useSystemSetup from '../../hooks/system/useSystemSetup';
import useSystemStatus from '../../hooks/system/useSystemStatus';
import useSystemUpdate from '../../hooks/system/useSystemUpdate';
import { setSystem } from '../../states/app/staticSlice';
import { setProfile } from '../../states/login/loginSlice';
import { FormEndMEP } from './components/FormEndMEP.jsx';
import { FormMEP } from './components/FormMEP.jsx';
import { FormUpdateMEP } from './components/FormUpdateMEP.jsx';
import { HelperMEP } from './components/HelperMEP.jsx';
import { PatchNoteTable } from './components/PatchNoteTable.jsx';
import { StatusEmote } from './components/StatusEmote.jsx';

export const MEPInterface = () => {
  const profile = useSelector((state) => state?.login?.profile);
  const { system, mutate, isLoading, isValidating } = useSystemStatus();
  const [component, setComponent] = useState(null);
  const [activeStep, setActiveStep] = useState(0);
  const [completed, setCompleted] = useState([]);
  const { systemUpdate } = useSystemUpdate();
  const { systemCancel } = useSystemCancel();
  const { systemSetup } = useSystemSetup();
  const { setStartDeploy } = useStartDeploy();
  const { setEndDeploy } = useEndDeploy();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [openHelper, setOpenHelper] = useState(false);

  const defineColor = (id) => {
    let color = 'info';
    if (activeStep === id) {
      color = 'primary';
    }
    if (completed.includes(id)) {
      color = 'success';
    }
    return color;
  };

  const actions = [
    {
      icon: <ManageHistoryIcon fontSize="large" color={defineColor(0) || ''} />,
      textContent: 'Programmer un déploiment',
      onClick: () => setComponent('Form'),
      isDisabled: system?.status?.code != 'ACTIVE',
    },
    {
      icon: (
        <SettingsSuggestIcon fontSize="large" color={defineColor(1) || ''} />
      ),
      textContent: 'Lancer / Modifier le déploiement',
      onClick: () => setComponent('Deploy'),
      isDisabled: system?.status?.code != 'WAITING_DEPLOY',
    },
    {
      icon: <LockOpenIcon fontSize="large" color={defineColor(2) || ''} />,
      textContent: 'Finaliser le déploiement',
      onClick: () => setComponent('End'),
      isDisabled: system?.status?.code != 'DEPLOYING',
    },
  ];

  const setupDeploy = async (update) => {
    await systemSetup(update);
    mutate();
  };

  const cancelDeploy = async (update) => {
    await systemCancel(update);
    mutate();
  };

  const updateDeploy = async (update) => {
    await systemUpdate(update);
    mutate();
  };

  const startDeploy = async (update) => {
    await setStartDeploy(update);
    mutate();
    googleLogout();
    dispatch(setProfile(null));
    navigate('/login');
  };

  const endDeploy = async (update) => {
    await setEndDeploy(update);
    mutate();
  };

  useEffect(() => {
    switch (system?.status?.code) {
      case 'ACTIVE':
        setActiveStep(0);
        setCompleted([]);
        break;
      case 'WAITING_DEPLOY':
        setActiveStep(1);
        setCompleted([0]);
        break;
      case 'DEPLOYING':
        setActiveStep(2);
        setCompleted([0, 1]);
        break;
      default:
        break;
    }
    if (!isLoading && !isValidating) {
      dispatch(setSystem(system));
    }
  }, [system, isLoading, isValidating]);

  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-start', gap: 2 }}>
          <StatusEmote code={system?.status?.code} />
          <Typography
            color="primary"
            sx={{ fontSize: '1.5em', fontWeight: 'bold' }}
          >
            Statut actuel de Pandora: {system?.status?.name}
          </Typography>
        </Box>
        <Button onClick={() => setOpenHelper(true)}>
          <HelpOutlineIcon />
        </Button>
      </Box>
      <HelperMEP isOpen={openHelper} handleClose={() => setOpenHelper(false)} />
      <Stepper alternativeLabel sx={{ m: 2 }} activeStep={activeStep}>
        {actions.map((action) => (
          <Step key={action.textContent} disabled={action.isDisabled}>
            <StepButton
              onClick={() => action.onClick()}
              icon={action.icon}
              sx={{ p: '16px' }}
            >
              {action.textContent}
            </StepButton>
          </Step>
        ))}
      </Stepper>
      {component === 'Form' && (
        <FormMEP
          data={system}
          onSubmit={(update) => setupDeploy(update)}
          onClose={() => setComponent(null)}
          profile={profile}
        />
      )}
      {component === 'Deploy' && (
        <FormUpdateMEP
          data={system}
          onUpdate={(update) => updateDeploy(update)}
          onCancel={(update) => cancelDeploy(update)}
          systemChange={(update) => startDeploy(update)}
          onClose={() => setComponent(null)}
          profile={profile}
        />
      )}
      {component === 'End' && (
        <FormEndMEP
          data={system}
          onClose={() => setComponent(null)}
          onSubmit={(update) => endDeploy(update)}
          profile={profile}
        />
      )}
      <PatchNoteTable displayed={component === null} />
    </>
  );
};
